<template>
  <div
    class="col-sm-12 px-0"
  >
    <div
      v-if="error"
      class="settings-box"
    >
      <span
        class="text-danger small"
      >
        {{ $t('error.settingsUnavailable') }}
      </span>
    </div>

    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="settings-title pb-2">
        {{ $t('settings.notify.billUpdates') }}
      </div>

      <SwitchWithDescription
        class="mt-4"
        :title="$t('settings.notify.sendBillUpdates')"
        :description="$t('settings.info.sendBillUpdates')"
        :pending="preferencesPending || error"
        :value="preferences['BlockBillUpdates'] !== 'true'"
        @switch="update('BlockBillUpdates', (!$event).toString())"
      />

      <SwitchWithDescription
        class="mt-4"
        :title="$t('settings.notify.sendBillUpdatesToInactive')"
        :description="$t('settings.info.sendBillUpdatesToNotInvited')"
        :pending="preferencesPending || error"
        :value="preferences['SendInactiveBillUpdates'] === 'true'"
        @switch="update('SendInactiveBillUpdates', $event.toString())"
      />
    </div>

    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="settings-title pb-2">
        {{ $t('settings.notify.billingScopeSummary') }}
      </div>
      <SwitchWithDescription
        class="mt-2"
        :title="$t('settings.notify.sendBillingScopeSummary')"
        :description="$t('settings.info.sendBillingScopeSummary')"
        :pending="preferencesPending || error"
        :value="preferences['SendBillingScopeSummary'] === 'true'"
        @switch="update('SendBillingScopeSummary', $event.toString())"
      />

      <ScopeDaySelect
        style="max-width: 370px;"
        :init-value="preferences['BillingScopeSummaryDay']"
        :pending="preferencesPending || error"
        @update="update('BillingScopeSummaryDay', $event.toString())"
      />
    </div>

    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div
        v-if="preferencesPending"
        class="position-absolute text-center"
        style="top: 20px; width: 100%;"
      >
        <Loader />
      </div>
      <div class="settings-title pb-2">
        {{ $t('settings.notify.reminders') }}
      </div>

      <SwitchWithDescription
        class="mt-2"
        :title="$t('settings.notify.sendBillDueDateExpired')"
        :description="$t('settings.info.sendBillDueDateExpired')"
        :pending="preferencesPending || error"
        :value="preferences['SendBillDueDateExpired'] === 'true'"
        @switch="update('SendBillDueDateExpired', $event.toString())"
      />

      <div
        class="settings-title mt-4 mb-2 d-flex"
      >
        {{ $t('settings.remindLatePayments') }}
        <TooltipHelp class="pl-3">
          {{ $t('settings.info.remindLatePayments') }}
        </TooltipHelp>
      </div>
      <div class="row container-small mt-3">
        <div>
          <BFormRadioGroup
            :checked="preferences['RemindersPolicy']"
            :disabled="preferencesPending || error"
            class="ml-3"
            stacked
            @change="update('RemindersPolicy', $event)"
          >
            <BFormRadio
              name="notify-email"
              value="EveryThreeDays"
            >
              {{ $t('settings.notify.every3days') }}
            </BFormRadio>
            <BFormRadio
              name="notify-email"
              value="EveryWeek"
            >
              {{ $t('settings.notify.everyWeek') }}
            </BFormRadio>
            <BFormRadio
              name="notify-email"
              value="EveryTwoWeeks"
            >
              {{ $t('settings.notify.everyTwoWeeks') }}
            </BFormRadio>
            <BFormRadio
              name="notify-email"
              value="EveryMonth"
            >
              {{ $t('settings.notify.everyMonth') }}
            </BFormRadio>
            <BFormRadio
              name="notify-email"
              value="Never"
            >
              {{ $t('settings.notify.never') }}
            </BFormRadio>
          </BFormRadioGroup>
        </div>
      </div>

      <DaySelect
        style="max-width: 370px;"
        :init-value="preferences['RemindersFirstDay']"
        :pending="preferencesPending || error"
        @update="update('RemindersFirstDay', $event.toString())"
      />
    </div>

    <div
      class="settings-box"
      :class="{ disabled: error }"
    >
      <div class="settings-title pb-2">
        {{ $t('settings.notify.smsNotifications') }}
      </div>
      <SwitchWithDescription
        :title="$t('settings.notify.sendRemindersBySms')"
        :description="$t('settings.info.sendRemindersBySms')"
        :pending="preferencesPending || error"
        :value="preferences['SendSmsReminders'] === 'true'"
        @switch="update('SendSmsReminders', $event.toString())"
      />
      <SwitchWithDescription
        :title="$t('settings.notify.sendBillUpdates')"
        :description="$t('settings.info.sendBillUpdatesBySms')"
        :pending="preferencesPending || error"
        :value="preferences['SendSmsBillUpdates'] === 'true'"
        @switch="update('SendSmsBillUpdates', $event.toString())"
      />
      <div
        class="mb-1 mt-3 d-flex align-items-center"
      >
        <div class="pr-3">
          <small>
            {{ $t('settings.notify.smsCount') }}:
          </small>
        </div>
        <TooltipHelp class="pl-3">
          {{ $t('settings.info.smsCount') }}
        </TooltipHelp>
      </div>

      <div
        class="font-weight-bold pl-2"
        style="font-size: 30px; width: 120px"
      >
        {{ limits ? (limits.tokensCredit - limits.tokensUsed) : 0 }}
      </div>
      <div class="text-secondary">
        / {{ limits ? (limits.tokensCredit || 0) : 0 }}
      </div>

      <div class="mt-3">
        <BButton
          size="sm"
          variant="outline-primary"
          :disabled="topUpPending || error"
          @click="openTopUp"
        >
          {{ $t('settings.notify.addSms') }}
        </BButton>
      </div>

      <PModal v-model="showTopUp">
        <div class="p-2">
          <div class="settings-title mb-3">
            {{ $t('settings.notify.addSms') }}
          </div>

          <label class="small mb-1">
            {{ $t('settings.notify.smsTopUpAmount') }}
          </label>
          <BFormInput
            v-model.number="topUpAmount"
            type="number"
            min="1"
            step="1"
            style="max-width: 200px"
          />

          <div class="d-flex flex-wrap mt-2">
            <BButton
              v-for="amount in topUpAmounts"
              :key="amount"
              size="sm"
              variant="outline-secondary"
              class="mr-2 mb-1"
              @click="topUpAmount = amount"
            >
              +{{ amount }}
            </BButton>
          </div>

          <p class="mt-3 mb-1 font-weight-bold">
            {{ $t('settings.notify.smsTopUpInfo', { amount: topUpCost }) }}
          </p>
          <p class="text-danger small">
            {{ $t('settings.notify.smsTopUpIrreversible') }}
          </p>

          <div class="d-flex justify-content-end mt-3">
            <BButton
              variant="link"
              class="mr-2"
              :disabled="topUpPending"
              @click="showTopUp = false"
            >
              {{ $t('general.cancel') }}
            </BButton>
            <BButton
              variant="primary"
              :disabled="topUpPending || !isTopUpValid"
              @click="buySms(topUpAmount)"
            >
              {{ $t('settings.notify.addSms') }}
            </BButton>
          </div>
        </div>
      </PModal>

      <div class="mt-4">
        <div class="small text-secondary mb-1">
          <template v-if="recurring && recurring.enabled">
            {{ $t('settings.notify.recurringTopUpInfo', {
              amount: recurring.tokens,
              date: recurring.nextTopUp ? formatDate(recurring.nextTopUp) : '',
            }) }}
          </template>
          <template v-else>
            {{ $t('settings.notify.recurringTopUpOff') }}
          </template>
        </div>
        <BButton
          size="sm"
          variant="outline-primary"
          :disabled="recurringPending || error"
          @click="openRecurring"
        >
          {{ $t('settings.notify.recurringTopUpConfigure') }}
        </BButton>
      </div>

      <PModal v-model="showRecurring">
        <div class="p-2">
          <div class="settings-title mb-3">
            {{ $t('settings.notify.recurringTopUp') }}
          </div>

          <BFormCheckbox
            v-model="recurringEnabled"
            switch
            class="mb-3"
          >
            {{ $t('settings.notify.recurringTopUpEnable') }}
          </BFormCheckbox>

          <template v-if="recurringEnabled">
            <label class="small mb-1">
              {{ $t('settings.notify.smsTopUpAmount') }}
            </label>
            <BFormInput
              v-model.number="recurringAmount"
              type="number"
              min="1"
              step="1"
              style="max-width: 200px"
            />

            <div class="d-flex flex-wrap mt-2">
              <BButton
                v-for="amount in topUpAmounts"
                :key="amount"
                size="sm"
                variant="outline-secondary"
                class="mr-2 mb-1"
                @click="recurringAmount = amount"
              >
                +{{ amount }}
              </BButton>
            </div>

            <p class="mt-3 mb-1 font-weight-bold">
              {{ $t('settings.notify.recurringTopUpCost', { amount: recurringCost }) }}
            </p>
            <p class="text-secondary small">
              {{ $t('settings.notify.recurringTopUpSchedule') }}
            </p>
          </template>

          <div class="d-flex justify-content-end mt-3">
            <BButton
              variant="link"
              class="mr-2"
              :disabled="recurringPending"
              @click="showRecurring = false"
            >
              {{ $t('general.cancel') }}
            </BButton>
            <BButton
              variant="primary"
              :disabled="recurringPending || !isRecurringValid"
              @click="saveRecurring"
            >
              {{ $t('general.save') }}
            </BButton>
          </div>
        </div>
      </PModal>

      <div class="settings-title mt-4 mb-2">
        {{ $t('settings.notify.smsUsageHistory') }}
      </div>
      <BarChart
        v-if="monthlyUsage.length"
        :values="monthlyUsage"
      />
    </div>

    <div
      class="settings-box mb-4"
      :class="{ disabled: error }"
    >
      <div class="settings-title pb-2">
        {{ $t('settings.notify.uninvitedPayers') }}
      </div>
      <SwitchWithDescription
        :title="$t('settings.notify.sendReminders')"
        :description="$t('settings.info.sendRemindersToNotInvited')"
        :pending="preferencesPending || error"
        :value="preferences['SendInactiveReminders'] === 'true'"
        @switch="update('SendInactiveReminders', $event.toString())"
      />
      <SwitchWithDescription
        class="mt-4"
        :title="$t('settings.notify.sendNewMessages')"
        :description="$t('settings.info.sendNewMessagesToNotInvited')"
        :pending="preferencesPending || error"
        :value="preferences['SendInactiveMessages'] === 'true'"
        @switch="update('SendInactiveMessages', $event.toString())"
      />
    </div>
  </div>
</template>

<script>
import SwitchWithDescription from '@/components/settings/SwitchWithDescription';
import DaySelect from '@/components/settings/notify/DaySelect';
import ScopeDaySelect from '@/components/settings/notify/ScopeDaySelect';
import BarChart from '@/components/charts/BarChart';
import PModal from '@/components/utils/PModal';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

const SMS_UNIT_PRICE = 0.10;

export default {
  data: () => ({
    emailNotify: '',
    preferences: {},
    limits: null,
    monthlyUsage: [],
    error: false,
    smsPending: false,
    preferencesPending: false,
    initBillingScopeSummaryDay: '1',
    billingScopeDaySuccess: false,
    showTopUp: false,
    topUpPending: false,
    topUpAmount: 100,
    topUpAmounts: [100, 500, 1000, 5000],
    recurring: null,
    showRecurring: false,
    recurringPending: false,
    recurringEnabled: false,
    recurringAmount: 100,
  }),
  components: {
    SwitchWithDescription,
    ScopeDaySelect,
    DaySelect,
    BarChart,
    PModal,
  },
  computed: {
    ...mapGetters(['payeeId']),
    isTopUpValid() {
      return Number.isInteger(this.topUpAmount) && this.topUpAmount > 0;
    },
    topUpCost() {
      const amount = this.isTopUpValid ? this.topUpAmount : 0;
      return (amount * SMS_UNIT_PRICE).toFixed(2);
    },
    isRecurringValid() {
      return !this.recurringEnabled
        || (Number.isInteger(this.recurringAmount) && this.recurringAmount > 0);
    },
    recurringCost() {
      const amount = Number.isInteger(this.recurringAmount) && this.recurringAmount > 0
        ? this.recurringAmount
        : 0;
      return (amount * SMS_UNIT_PRICE).toFixed(2);
    },
  },
  methods: {
    ...mapActions([
      'getPayeeNotifyPreferences',
      'setPayeeNotifyPreferences',
      'getSmsUsage',
      'getSmsMonthlyUsage',
      'addSmsCredits',
      'getSmsRecurringTopUp',
      'setSmsRecurringTopUp',
    ]),
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    openTopUp() {
      const [defaultAmount] = this.topUpAmounts;
      this.topUpAmount = defaultAmount;
      this.showTopUp = true;
    },
    openRecurring() {
      const [defaultAmount] = this.topUpAmounts;
      this.recurringEnabled = !!(this.recurring && this.recurring.enabled);
      this.recurringAmount = (this.recurring && this.recurring.tokens) || defaultAmount;
      this.showRecurring = true;
    },
    saveRecurring() {
      if (!this.isRecurringValid) {
        return;
      }
      this.recurringPending = true;
      const tokens = this.recurringEnabled ? this.recurringAmount : 0;
      this.setSmsRecurringTopUp({ data: { tokens, enabled: this.recurringEnabled } })
        .then(({ data }) => {
          this.recurring = data;
          this.showRecurring = false;
        })
        .finally(() => {
          this.recurringPending = false;
        });
    },
    buySms(tokens) {
      if (!this.isTopUpValid) {
        return;
      }
      this.topUpPending = true;
      this.addSmsCredits({ data: { tokens } })
        .then(({ data }) => {
          this.limits = data;
          this.showTopUp = false;
        })
        .finally(() => {
          this.topUpPending = false;
        });
    },
    update(key, value) {
      this.preferencesPending = true;
      this.setPayeeNotifyPreferences({
        data: [{
          key,
          value,
        }],
      })
        .then(({ data }) => {
          this.init(data);
        })
        .finally(() => {
          this.preferencesPending = false;
        });
    },
    init(data) {
      this.preferences = data.reduce((acc, curr) => {
        this.$set(acc, curr.key, curr.value);
        return acc;
      }, {});

      this.$set(this.preferences, 'BillingScopeSummaryDay', this.preferences.BillingScopeSummaryDay || 1);
      this.initBillingScopeSummaryDay = this.preferences.BillingScopeSummaryDay;
    },
    request() {
      this.smsPending = true;
      this.getSmsUsage()
        .then(({ data }) => {
          this.limits = data;
        })
        .finally(() => {
          this.smsPending = false;
        });

      this.getSmsMonthlyUsage()
        .then(({ data }) => {
          this.monthlyUsage = (data.entries || []).map((x) => ({
            label: `${x.month}/${x.year}`,
            value: x.tokensUsed,
            tooltip: this.$t('settings.notify.smsInMonth', { count: x.tokensUsed }),
          }));
        })
        .catch(() => {
          this.monthlyUsage = [];
        });

      this.getSmsRecurringTopUp()
        .then(({ data }) => {
          this.recurring = data;
        })
        .catch(() => {
          this.recurring = null;
        });

      this.preferencesPending = true;
      this.error = false;
      this.getPayeeNotifyPreferences()
        .then(({ data }) => {
          this.init(data);
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.preferencesPending = false;
        });
    },
  },
  created() {
    this.$emit('page', 'notifications');
    this.request();
  },
};
</script>

<style lang="scss" scoped>
  .time-text {
    line-height: 1.1;
    font-size: 13px;
  }
</style>
